import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import codeValidationService from '../services/CodeValidationService';
import { setInvitationMetaTags, resetDefaultMetaTags } from '../utils/metaTags';

interface InvitationLandingPageProps {
  onRegisterClick: (code: string) => void;
}

export const InvitationLandingPage: React.FC<InvitationLandingPageProps> = ({ onRegisterClick }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [invitationCode, setInvitationCode] = useState<string>('');
  const [codeValidation, setCodeValidation] = useState<{
    isValidating: boolean;
    isValid: boolean | null;
    message: string;
    codeDetails?: any;
  }>({
    isValidating: false,
    isValid: null,
    message: ''
  });

  useEffect(() => {
    // Parse code from URL
    const code = codeValidationService.parseCodeFromUrl();
    if (code) {
      setInvitationCode(code);
      validateCode(code);
      // Store code in sessionStorage for persistence
      sessionStorage.setItem('inviteCode', code);
    } else {
      // Check if code exists in sessionStorage
      const storedCode = sessionStorage.getItem('inviteCode');
      if (storedCode) {
        setInvitationCode(storedCode);
        validateCode(storedCode);
      } else {
        // If no code in URL or storage, redirect to home
        navigate('/');
      }
    }

    // Cleanup meta tags when component unmounts
    return () => {
      resetDefaultMetaTags();
    };
  }, [location, navigate]);

  const validateCode = async (code: string) => {
    setCodeValidation({
      isValidating: true,
      isValid: null,
      message: 'Validating your invitation...'
    });

    try {
      const result = await codeValidationService.validateCode(code);
      
      setCodeValidation({
        isValidating: false,
        isValid: result.isValid,
        message: result.isValid 
          ? `Welcome! This invitation${result.description ? ` (${result.description})` : ''} is valid.` 
          : result.error || 'This invitation code is not valid.',
        codeDetails: result.isValid ? result : undefined
      });

      // Update meta tags for social sharing when code is valid
      if (result.isValid) {
        setInvitationMetaTags(code, result);
      }
    } catch (error) {
      setCodeValidation({
        isValidating: false,
        isValid: false,
        message: 'Failed to validate invitation. Please try again.'
      });
    }
  };

  const handleUseModal = () => {
    if (codeValidation.isValid && invitationCode) {
      onRegisterClick(invitationCode);
    }
  };

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0a] via-[#141414] to-[#0a0a0a] flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Main Card */}
        <div className="bg-[#141414] rounded-lg shadow-xl p-8 text-center">
          {/* Header */}
          <div className="mb-6">
            <div className="w-16 h-16 bg-gradient-brand rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 19v-8.93a2 2 0 01.89-1.664l7-4.666a2 2 0 012.22 0l7 4.666A2 2 0 0121 10.07V19M3 19a2 2 0 002 2h14a2 2 0 002-2M3 19l6.75-4.5M21 19l-6.75-4.5M12 2v6.5" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">You’re invited to Bilt:Spec</h1>
            <p className="text-gray-400">Collect cars you spot and document the cars you build. Join Bilt with this invite.</p>
          </div>

          {/* Code Display */}
          <div className="mb-6">
            <div className="form-section">
              <div className="flex items-center justify-center space-x-2">
                <span className="text-gray-400 text-sm">Invitation Code:</span>
                <span className="text-white font-mono text-lg">{invitationCode}</span>
              </div>
            </div>
          </div>

          {/* Validation Status */}
          <div className="mb-6">
            {codeValidation.isValidating ? (
              <div className="flex items-center justify-center space-x-2 text-blue-400">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                <span className="text-sm">{codeValidation.message}</span>
              </div>
            ) : codeValidation.isValid === true ? (
              <div className="space-y-2">
                <div className="flex items-center justify-center space-x-2 text-green-400">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-sm">{codeValidation.message}</span>
                </div>
                {(codeValidation.codeDetails?.grants_can_organize || codeValidation.codeDetails?.grants_can_judge) ? (
                  <p className="text-sm text-orange-300/90">
                    This invite includes
                    {codeValidation.codeDetails?.grants_can_organize ? ' organizer' : ''}
                    {codeValidation.codeDetails?.grants_can_organize && codeValidation.codeDetails?.grants_can_judge ? ' and' : ''}
                    {codeValidation.codeDetails?.grants_can_judge ? ' judge' : ''}
                    {' '}access.
                  </p>
                ) : null}
              </div>
            ) : codeValidation.isValid === false ? (
              <div className="flex items-center justify-center space-x-2 text-red-400">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span className="text-sm">{codeValidation.message}</span>
              </div>
            ) : null}
          </div>



          {/* Action Buttons */}
          <div className="space-y-3">
            {codeValidation.isValid ? (
              <>
                <button
                  onClick={handleUseModal}
                  className="w-full bg-gradient-brand hover:from-orange-500 hover:to-orange-800 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105"
                >
                  Join Bilt
                </button>
                <p className="text-gray-400 text-sm">
                  Already have an account?{' '}
                  <button
                    onClick={handleGoHome}
                    className="text-orange-400 hover:text-orange-300 underline"
                  >
                    Sign in
                  </button>
                </p>
              </>
            ) : (
              <button
                onClick={handleGoHome}
                className="w-full bg-[#1a1a1a] hover:bg-white/[0.06] text-white font-semibold py-3 px-6 rounded-lg transition-colors"
              >
                Go to Bilt:Spec
              </button>
            )}
          </div>

          {/* Features Preview */}
          {codeValidation.isValid && (
            <div className="mt-8 pt-6 border-t border-white/[0.08]">
              <h3 className="text-white font-medium mb-4">What comes with your garage</h3>
              <div className="space-y-2 text-sm text-gray-400">
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>A public profile for your vehicle</span>
                </div>
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Import and organize your build list</span>
                </div>
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>QR for shows and sharing</span>
                </div>
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Event registration tied to the same build</span>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-white/[0.08]">
            <p className="text-xs text-gray-500">
              Invite-only while Bilt:Spec is in beta
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}; 